import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface PaybackData {
  measure: string;
  investmentCost: number;
  annualSavings: number;
  subsidies: number;
  financingType: 'cash' | 'loan';
  loanRate?: number;
  loanTerm?: number;
}

interface PaybackResult {
  measure: string;
  paybackYears: number;
  paybackMonths: number;
  roi10Years: number;
  roi20Years: number;
  totalSavings10Years: number;
  totalSavings20Years: number;
  netInvestment: number;
  monthlyPayment?: number;
  totalLoanCost?: number;
  yearlyData: {
    year: number;
    cumulativeSavings: number;
    cumulativePayments: number;
    netPosition: number;
  }[];
}

export const usePaybackCalculation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [paybackResults, setPaybackResults] = useState<PaybackResult[]>([]);

  const calculatePayback = (data: PaybackData): PaybackResult => {
    const netInvestment = data.investmentCost - data.subsidies;
    let paybackYears = 0;
    let monthlyPayment = 0;
    let totalLoanCost = 0;
    
    if (data.financingType === 'loan' && data.loanRate && data.loanTerm) {
      const monthlyRate = data.loanRate / 100 / 12;
      const numPayments = data.loanTerm * 12;
      monthlyPayment = (netInvestment * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
                      (Math.pow(1 + monthlyRate, numPayments) - 1);
      totalLoanCost = monthlyPayment * numPayments;
      
      // For loans, calculate when savings exceed monthly payment
      paybackYears = Math.max(data.loanTerm, netInvestment / data.annualSavings);
    } else {
      // Cash payment
      paybackYears = netInvestment / data.annualSavings;
    }

    // Generate yearly data for charts
    const yearlyData = [];
    let cumulativeSavings = 0;
    let cumulativePayments = data.financingType === 'loan' ? 0 : netInvestment;
    
    for (let year = 1; year <= 20; year++) {
      cumulativeSavings += data.annualSavings;
      
      if (data.financingType === 'loan' && year <= (data.loanTerm || 0)) {
        cumulativePayments += monthlyPayment * 12;
      }
      
      yearlyData.push({
        year,
        cumulativeSavings,
        cumulativePayments,
        netPosition: cumulativeSavings - cumulativePayments
      });
    }

    const totalSavings10Years = data.annualSavings * 10;
    const totalSavings20Years = data.annualSavings * 20;
    
    const investment10 = data.financingType === 'loan' && data.loanTerm && data.loanTerm <= 10 
      ? totalLoanCost : netInvestment;
    const investment20 = data.financingType === 'loan' 
      ? totalLoanCost : netInvestment;

    return {
      measure: data.measure,
      paybackYears: Math.round(paybackYears * 10) / 10,
      paybackMonths: Math.round(paybackYears * 12),
      roi10Years: Math.round(((totalSavings10Years - investment10) / investment10) * 100),
      roi20Years: Math.round(((totalSavings20Years - investment20) / investment20) * 100),
      totalSavings10Years: Math.round(totalSavings10Years),
      totalSavings20Years: Math.round(totalSavings20Years),
      netInvestment: Math.round(netInvestment),
      monthlyPayment: monthlyPayment ? Math.round(monthlyPayment) : undefined,
      totalLoanCost: totalLoanCost ? Math.round(totalLoanCost) : undefined,
      yearlyData
    };
  };

  const calculateMultiplePaybacks = async (dataArray: PaybackData[]) => {
    setIsLoading(true);
    
    try {
      const results = dataArray.map(data => calculatePayback(data));
      setPaybackResults(results);

      // Save scenarios to database for analytics
      for (const result of results) {
        await supabase.from('payback_scenarios').insert({
          measure_type: result.measure.toLowerCase().replace(/\s+/g, '_'),
          investment_cost: result.netInvestment,
          annual_savings: dataArray.find(d => d.measure === result.measure)?.annualSavings || 0,
          subsidies: dataArray.find(d => d.measure === result.measure)?.subsidies || 0,
          financing_type: dataArray.find(d => d.measure === result.measure)?.financingType || 'cash',
          loan_rate: dataArray.find(d => d.measure === result.measure)?.loanRate || 0,
          loan_term: dataArray.find(d => d.measure === result.measure)?.loanTerm || 0,
          payback_years: result.paybackYears,
          total_savings_10y: result.totalSavings10Years,
          total_savings_20y: result.totalSavings20Years,
          roi_percentage: result.roi20Years
        });
      }

    } catch (error) {
      console.error('Error calculating payback:', error);
      setPaybackResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getInvestmentCosts = (measure: string, capacity?: number, area?: number): number => {
    // Average investment costs in Netherlands (2025)
    const costs = {
      'zonnepanelen': 1200, // per kWp
      'warmtepomp': 12000, // base cost for average home
      'dakisolatie': 35, // per m²
      'muurisolatie': 85, // per m²
      'vloerisolatie': 25, // per m²
    };

    const measureKey = measure.toLowerCase().replace(/\s+/g, '');
    const baseCost = costs[measureKey as keyof typeof costs] || 0;

    if (measure.includes('zonnepanelen') && capacity) {
      return baseCost * capacity;
    }
    
    if (measure.includes('isolatie') && area) {
      return baseCost * area;
    }

    return baseCost;
  };

  return {
    calculateMultiplePaybacks,
    calculatePayback,
    getInvestmentCosts,
    isLoading,
    paybackResults
  };
};